import './QuotationPage.scss';
import { Form } from 'antd';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
import QuotationHeader from '../../components/quotation/QuotationHeader/QuotationHeader';
import QuotationFilter from '../../components/quotation/QuotationFilter/QuotationFilter';
import QuotationTable from '../../components/quotation/QuotationTable/QuotationTable';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProductPricing } from '../../store/actions/quotation.action';
import ButtonPrimaryLarge from '../../components/common/ButtonPrimary/ButtonPrimaryLarge';
import ButtonSecondaryLarge from '../../components/common/ButtonSecondary/ButtonSecondaryLarge';
import { Icon } from '../../components/icon/icon';
import { openMessageWarning } from '../../components/common/Message/openMessageWarning';
import { createMathEval } from '../../utils/math/mathEval.utils';
import {
  exportQuotationPDF,
  printQuotationPDF,
} from '../../utils/quotation/exportQuotationPDF';
import QuotationHeaderPDF from '../../components/quotation/QuotationHeader/QuotationHeaderPDF';
import QuotationFilterPDF from '../../components/quotation/QuotationFilter/QuotationFilterPDF';
import QuotationTablePDF from '../../components/quotation/QuotationTable/QuotationTablePDF';
import { roundTo } from '../../utils/geometry/epsilon';
import { CONST } from '../../constants/constant';

const QuotationPage = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [data, setData] = useState([]);
  const supplierSelectRef = useRef();
  const [downloadPDF, setDownloadPDF] = useState(false);
  const [printMode, setPrintMode] = useState(false);

  const supplierWatch = Form.useWatch('supplier', form);

  const { productNames, suppliers, materialTypes, materialDescription, unit } =
    useSelector(state => state.quotation);

  const dispatch = useDispatch();

  const handlePrint = async () => {
    setPrintMode(true);
    setDownloadPDF(true);
  };

  const handleChangeValues = useCallback(
    values => {
      const key = values[0]?.name?.[0];
      const fieldChanged = values[0]?.name?.[1];
      const fieldValueChanged = form.getFieldValue(key) ?? {};

      if (
        [
          'product_name',
          'material_type',
          'material_description',
          'group',
          'unit',
        ].includes(fieldChanged)
      ) {
        // kiểm tra bộ key có hợp lệ không
        const {
          product_name,
          material_type,
          material_description,
          unit,
          group,
        } = fieldValueChanged;
        if (supplierWatch) {
          dispatch(
            fetchProductPricing({
              key,
              params: {
                product_name,
                material_type,
                material_description,
                supplier: supplierWatch,
                unit,
                group,
              },
            })
          ).then(action => {
            if (action.payload?.data) {
              const products = action.payload.data.list_products;
              const filterInfo = action.payload.data.filter_info || {};
              const optionsProductNames = filterInfo.product_name || [];
              const optionsMaterialTypes = filterInfo.material_type || [];
              const optionsMaterialDescriptions =
                filterInfo.material_description || [];
              const optionsUnit = filterInfo.unit || [];
              const optionsGroup = filterInfo.groups || [];

              // kiểm tra nếu chỉ có 1 sản phẩm phù hợp với lựa chọn của user thì tự động điền thông tin sản phẩm đó vào bảng, nếu có nhiều hơn 1 sản phẩm phù hợp thì hiện cảnh báo yêu cầu user chọn thêm thông tin để lọc ra đúng sản phẩm, nếu không có sản phẩm nào phù hợp thì hiện cảnh báo không tìm thấy dữ liệu
              if (
                products.length === 1 &&
                ((!product_name && !optionsProductNames.length) ||
                  optionsProductNames.includes(product_name)) &&
                ((!material_type && !optionsMaterialTypes.length) ||
                  optionsMaterialTypes.includes(material_type)) &&
                ((!material_description &&
                  !optionsMaterialDescriptions.length) ||
                  optionsMaterialDescriptions.includes(material_description)) &&
                ((!unit && !optionsUnit.length) ||
                  optionsUnit.includes(unit)) &&
                ((!group && !optionsGroup.length) ||
                  optionsGroup.includes(group))
              ) {
                const product = products?.[0];

                setData(prev =>
                  prev.map(item =>
                    Number(item.key) === Number(key)
                      ? {
                          ...item,
                          product_name: product_name,
                          material_type: material_type,
                          material_description: material_description,
                          group: group,
                          unit: unit,
                          ...product,
                          size: JSON.parse(product.size),
                        }
                      : item
                  )
                );
                form.setFieldsValue({
                  [key]: {
                    ...fieldValueChanged,
                    size: JSON.parse(product.size),
                    quantity_formula: product.quantity_formula,
                    quantity: product.quantity ?? fieldValueChanged.quantity,
                    price: product.price,
                    product_name: product_name,
                    material_type: material_type,
                    material_description: material_description,
                    unit: unit,
                    supplier: supplierWatch,
                    group: group,
                  },
                });
              } else {
                setData(prev =>
                  prev.map(item =>
                    Number(item.key) === Number(key)
                      ? {
                          key: item.key,
                          product_name: undefined,
                          material_type: undefined,
                          material_description: undefined,
                          unit: undefined,
                          group: undefined,
                        }
                      : item
                  )
                );
                form.setFieldsValue({
                  [key]: {
                    ...fieldValueChanged,
                    size: {},
                    quantity_formula: undefined,
                    quantity: undefined,
                    price: undefined,
                    product_name: product_name,
                    material_type: material_type,
                    material_description: material_description,
                    unit: unit,
                    supplier: supplierWatch,
                    group: group,
                  },
                });
                if (products.length === 0) {
                  openMessageWarning({
                    content: t(
                      'No results match your selection. Please try different options'
                    ),
                  });
                } else {
                  openMessageWarning({
                    content: t(
                      'Multiple products match your selection. Please provide more details to narrow the results'
                    ),
                  });
                }
              }
            }
          });
        }
      } else if (['size', 'quantity'].includes(fieldChanged)) {
        // nếu không phải những field trên thì chỉ cần cập nhật lại data mà không cần gọi api
        const { length, height, depth } = fieldValueChanged.size || {};
        const quantityForm = fieldValueChanged.quantity ?? 0;
        const maxHeightLength = Math.max(length || 0, height || 0);
        const price = roundTo(
          createMathEval(fieldValueChanged.price || '', {
            length: Number(length) || 0,
            height: Number(height) || 0,
            depth: Number(depth) || 0,
            maxHeightLength,
          }),
          3
        );

        const quantity = (() => {
          if (fieldValueChanged.quantity_formula !== '-') {
            return roundTo(
              createMathEval(fieldValueChanged.quantity_formula, {
                length: Number(length) || 0,
                height: Number(height) || 0,
                depth: Number(depth) || 0,
                maxHeightLength,
              }),
              3
            );
          } else {
            return quantityForm;
          }
        })();

        const total = roundTo(quantity * price, 0);

        setData(prev =>
          prev.map(item =>
            Number(item.key) === Number(key)
              ? {
                  ...item,
                  price,
                  quantity,
                  total,
                }
              : item
          )
        );
      }
    },
    [productNames, materialTypes, materialDescription, unit, supplierWatch]
  );

  useEffect(() => {
    dispatch(fetchProductPricing({}));
  }, []);

  useEffect(() => {
    // reset table data khi đổi nhà cung cấp
    setData([]);
  }, [supplierWatch]);

  useEffect(() => {
    if (downloadPDF) {
      setTimeout(() => {
        if (printMode) {
          printQuotationPDF('quotation-page-pdf').catch(error => {
            openMessageWarning({
              content: t('Error printing PDF. Please try again'),
            });
          });
        } else {
          exportQuotationPDF('quotation-page-pdf').catch(error => {
            openMessageWarning({
              content: t('Error downloading PDF. Please try again'),
            });
          });
        }
        setDownloadPDF(false);
        setPrintMode(false);
      }, 500);
    }
  }, [downloadPDF]);

  return (
    <>
      <div className="quotation-page min-h-screen bg-gray-50">
        <Form
          form={form}
          onFieldsChange={values => handleChangeValues(values)}
          layout="vertical"
          className=" px-4 gap-6 flex flex-col"
        >
          {/* Header */}
          <QuotationHeader />

          {/* Filter Section */}
          <QuotationFilter supplierSelectRef={supplierSelectRef} />

          {/* Table Section */}

          <QuotationTable
            supplierSelectRef={supplierSelectRef}
            data={data}
            setData={setData}
            form={form}
          />

          {/* Action Buttons */}
          <div className="print:hidden grid md:grid-cols-8 grid-cols-1 gap-4 mt-24 lg:px-40 lg:py-32 p-16">
            <ButtonPrimaryLarge
              icon={<Icon.ICPrint />}
              onClick={handlePrint}
              title={t('quotation.print', 'In Báo Giá')}
              className="md:col-start-4"
            />

            <ButtonSecondaryLarge
              icon={<Icon.ICDownload />}
              onClick={() => {
                setDownloadPDF(true);
              }}
              title={t('quotation.download', 'Tải PDF')}
              className="md:col-start-5"
            />
          </div>

          {/* Footer Note */}
          <div className="mt-8 text-start text-sm text-gray-500 h-fit">
            <span className="text-body-small italic flex gap-4 justify-start items-start">
              Báo giá trên chỉ mang tính chất tham khảo do chưa có số liệu công
              trình thực tế. Vui lòng liên hệ sale account qua Fanpage Fitta:
              <a
                href={CONST.laNhaFacebook}
                className="text-blue-500 underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Fitta Facebook Page
              </a>
              hoặc hotline/zalo:
              <a
                href={CONST.laNhaZalo}
                className="text-blue-500 underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Fitta Zalo
              </a>
              để được tư vấn đo đạc chính xác nhất.
            </span>
          </div>
        </Form>
      </div>
      {downloadPDF && <RenderPDF form={form} data={data} />}
    </>
  );
};

export default QuotationPage;

const RenderPDF = ({ form, data }) => {
  const { t } = useTranslation();
  return (
    <div className="quotation-page-pdf min-h-screen bg-gray-50 -z-[1000] absolute max-w-[1123px] w-[1123px]">
      <div className=" px-4 gap-6 flex flex-col">
        {/* Header */}
        <QuotationHeaderPDF form={form} />

        {/* Filter Section */}
        <QuotationFilterPDF form={form} />

        {/* Table Section */}

        <QuotationTablePDF data={data} form={form} />

        {/* Footer Note */}
        <div className="mt-8 text-center text-sm text-gray-500 h-fit">
          <span className="text-body-small italic ">
            Báo giá trên chỉ mang tính chất tham khảo do chưa có số liệu công
            trình thực tế. Vui lòng liên hệ sale account qua Fanpage Fitta:
            <a
              href={CONST.laNhaFacebook}
              className="text-blue-500 underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Fitta Facebook Page
            </a>
            hoặc hotline/zalo:
            <a
              href={CONST.laNhaZalo}
              className="text-blue-500 underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Fitta Zalo
            </a>
            để được tư vấn đo đạc chính xác nhất.
          </span>
        </div>
      </div>
    </div>
  );
};
